"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { X, MapPin, BedDouble, Undo2 } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitize";
import { stripInlineHtml } from "@/lib/localized-field";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";
import { localizeHotelCity } from "@/lib/category-display";
import { resolveAmenityChips } from "@/lib/amenity-chips";
import { VOUCHER_BOOKING_FINALITY_CLAUSES } from "@/lib/legal-content/voucher-booking-finality";
import HotelMiniMap from "@/components/deal/HotelMiniMap";
import { imagePathKey } from "@/components/deal/hotelImage";
import { sfeerbeeldForHotel } from "@/lib/content/hotel-sfeerbeeld";
import { resolveSectionField } from "./resolve-section-field";

// resolveSectionField (extracted to ./resolve-section-field.js so it is
// unit-testable) resolves a description_struct section field across every
// storage shape the catalog has held:
//   • plain string   → returned as-is
//   • MultiLangValue → resolves locale, EN → NL → DE → FR fallback ladder
//     (Kartik #108, 2026-04-28: cms#155 renamed paragraphs→content and made
//      title/content per-locale objects)
//   • array          → legacy RateHawk `paragraphs: [..strings..]` shape,
//     each element resolved and joined (Kartik #40, 2026-08-31: package 115
//     hotels rendered no description because the array fell through to "")

/**
 * Resolve the best image array from hotel data.
 * Priority: images (RateHawk selected), images_ext (RateHawk extended), fallback empty.
 * Old site used hotel.images filtered by .selected, new API may provide images_ext with {size} template.
 */
function resolveImages(hotel) {
  // images from hotels_extra_details — array of { url, selected } objects
  if (Array.isArray(hotel.images) && hotel.images.length > 0) {
    // Filter to selected images if they have a .selected flag (like old site)
    const selected = hotel.images.filter((img) => img.selected !== false);
    return selected.length > 0 ? selected : hotel.images;
  }
  // images_ext from RateHawk API — array of { url } with {size} template
  if (Array.isArray(hotel.images_ext) && hotel.images_ext.length > 0) {
    return hotel.images_ext;
  }
  return [];
}

/**
 * Get a displayable URL from an image object.
 * Handles both plain URL strings and objects with .url property.
 * Replaces {size} template in RateHawk URLs.
 */
function getImageUrl(img, size = "1024x768") {
  const raw = typeof img === "string" ? img : img?.url;
  if (!raw) return "";
  return raw.replace("{size}", size);
}

/**
 * TD-blocks 2026-07-02: chip renderer for the amenity chips (list rows +
 * modal). Canonical chips only — labels come from t('voucher.amenity.
 * <key>') so raw RateHawk strings never render (see amenity-chips.js).
 * `hiddenOnMobile` implements the pure-CSS cap from the design: list
 * chips 5 and 6 get hidden sm:inline-flex (4 chips on mobile, 6 on sm+),
 * no JS measuring.
 */
function AmenityChip({ chip, t, hiddenOnMobile = false }) {
  const Icon = chip.icon;
  return (
    <span
      className={`${
        hiddenOnMobile ? "hidden sm:inline-flex" : "inline-flex"
      } items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-700`}
    >
      <Icon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
      {t(`voucher.amenity.${chip.key}`)}
    </span>
  );
}

/**
 * TD-blocks 2026-07-02: hotel-level cancellation policy for the modal.
 * `cancellation_policy` is a NEW additive flatten on the BFF's
 * getHotelsForVoucherDeals endpoint; absent on prod as of 2026-07-02 →
 * returns "" and the line hides. Same stripInlineHtml treatment as
 * promotion descriptions. This is where MULTI-hotel vouchers surface
 * per-hotel cancellation (single-hotel vouchers additionally get the
 * VoucherFlexStrip line under the hero price).
 */
function resolveHotelCancellationPolicy(hotel) {
  const raw = hotel?.cancellation_policy ?? hotel?.cancellationPolicy;
  if (typeof raw !== "string" || !raw.trim()) return "";
  return stripInlineHtml(raw).trim();
}

/**
 * Partner hotels section for voucher detail page.
 * Shows hotel names as a bulleted list with star ratings.
 * Clicking a hotel opens a detail modal with image gallery + description.
 */
export default function PartnerHotelsClient({
  hotels = [],
  hotelIds = [],
  hotelTitle,
  isVoucherContext = false,
  leadHotelId = null,
  dealCity = null,
}) {
  const { locale } = useSiteContext();
  const { t } = getTranslations(locale);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [activeImage, setActiveImage] = useState(0);

  const filteredHotels =
    hotelIds.length > 0
      ? hotels.filter((h) => hotelIds.includes(h.hotel_id))
      : hotels;
  // Optional opener: the lead hotel (when it is in the shown list) goes first,
  // the rest keeps its order. Without leadHotelId nothing changes.
  const leadIdx =
    leadHotelId != null
      ? filteredHotels.findIndex((h) => h.hotel_id === leadHotelId)
      : -1;
  const matchedHotels =
    leadIdx > 0
      ? [filteredHotels[leadIdx], ...filteredHotels.filter((_, i) => i !== leadIdx)]
      : filteredHotels;

  const closeModal = useCallback(() => {
    setSelectedHotel(null);
    setActiveImage(0);
  }, []);

  const baseImages = selectedHotel ? resolveImages(selectedHotel) : [];
  // A fixed sfeerfoto for this hotel goes first, without duplicate.
  const sfeerUrl = selectedHotel ? sfeerbeeldForHotel(selectedHotel.hotel_id) : null;
  const hotelImages = sfeerUrl
    ? [sfeerUrl, ...baseImages.filter((img) => imagePathKey(getImageUrl(img)) !== imagePathKey(sfeerUrl))]
    : baseImages;
  // TD-blocks 2026-07-02: full chip row (max 8) + cancellation line for
  // the modal. Voucher pages always show booking finality; regular hotel
  // contexts retain the per-hotel policy and hide the line when absent.
  const modalChips = selectedHotel ? resolveAmenityChips(selectedHotel, 8) : [];
  const modalCancellation = selectedHotel
    ? isVoucherContext
      ? VOUCHER_BOOKING_FINALITY_CLAUSES[locale] ||
        VOUCHER_BOOKING_FINALITY_CLAUSES.en
      : resolveHotelCancellationPolicy(selectedHotel)
    : "";

  // Keyboard navigation for modal gallery
  const handleKeyDown = useCallback((e) => {
    if (!selectedHotel || hotelImages.length <= 1) return;
    if (e.key === "ArrowRight") {
      setActiveImage((prev) => (prev + 1) % hotelImages.length);
    } else if (e.key === "ArrowLeft") {
      setActiveImage((prev) => (prev - 1 + hotelImages.length) % hotelImages.length);
    } else if (e.key === "Escape") {
      closeModal();
    }
  }, [selectedHotel, hotelImages.length, closeModal]);

  useEffect(() => {
    if (!selectedHotel) return;
    window.addEventListener("keydown", handleKeyDown);
    // Lock body scroll while the modal is open. Without this the page keeps
    // scrolling behind the modal, and a leaflet map further down the page
    // (the region map) scrolls into view and paints OVER the modal because
    // leaflet panes use a high z-index (Dennis 2026-06-03).
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [selectedHotel, handleKeyDown]);

  if (!matchedHotels.length) return null;

  return (
    <section className="py-8 px-4 sm:px-6 border border-gray-200 rounded-lg bg-white">
      <h2 className="text-[22px] sm:text-[1.7rem] font-semibold text-gray-950 mb-4 flex items-center gap-2">
        <BedDouble className="w-7 h-7" />
        {hotelTitle || t("voucher.partner_hotels")}
      </h2>

      <ul className="space-y-3">
        {matchedHotels.map((hotel) => {
          // TD-blocks 2026-07-02: Booking.com search-card pattern — top
          // amenities as icon chips per hotel row. Renders up to 6 chips
          // (chips 5+6 hidden on mobile via CSS, 4-chip mobile cap). A
          // hotel without amenity_groups (field absent on prod today,
          // empty, or zero keyword matches) renders exactly the pre-chip
          // single-row li: no empty chip row, no placeholder.
          const chips = resolveAmenityChips(hotel, 6);
          return (
            <li key={hotel.hotel_id}>
              <div
                onClick={() => {
                  setSelectedHotel(hotel);
                  setActiveImage(0);
                }}
                className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:text-blue-700"
              >
                <span className="text-blue-600 text-2xl sm:text-3xl leading-none">&bull;</span>
                <span className="text-black text-[15px] sm:text-[18px] font-medium">
                  {hotel.hotel_name}
                </span>
                <span className="flex text-yellow-400">
                  {[...Array(hotel.star_rating || 3)].map((_, i) => (
                    <svg
                      key={i}
                      className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </span>
              </div>
              {chips.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5 ml-6 sm:ml-8">
                  {chips.map((chip, i) => (
                    <AmenityChip
                      key={chip.key}
                      chip={chip}
                      t={t}
                      hiddenOnMobile={i >= 4}
                    />
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Hotel detail modal */}
      {selectedHotel && (
        <div
          className="fixed inset-0 z-[1100] bg-black/60 flex items-center justify-center px-3 sm:px-6"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative p-4 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-3 right-3 bg-gray-200 rounded-full p-2 z-10 hover:bg-gray-300 transition-colors"
              aria-label={t("common.close")}
            >
              <X className="w-4 h-4" />
            </button>

            <h3 className="text-lg sm:text-xl font-semibold text-center mb-1 text-gray-500">
              {t("voucher.partner_hotels.hotel_details")}
            </h3>

            {/* Image carousel */}
            {hotelImages.length > 0 && (
              <>
                <div className="relative w-full h-[220px] sm:h-[400px] rounded-lg overflow-hidden mb-3">
                  <Image
                    src={getImageUrl(hotelImages[activeImage], "1024x768")}
                    alt={selectedHotel.hotel_name}
                    fill
                    sizes="(max-width: 768px) 100vw, 900px"
                    priority
                    className="object-cover"
                  />
                  {/* Prev/Next arrows */}
                  {hotelImages.length > 1 && (
                    <>
                      <button
                        onClick={() => setActiveImage((prev) => (prev - 1 + hotelImages.length) % hotelImages.length)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full w-8 h-8 flex items-center justify-center text-gray-700 shadow"
                      >
                        &#10094;
                      </button>
                      <button
                        onClick={() => setActiveImage((prev) => (prev + 1) % hotelImages.length)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full w-8 h-8 flex items-center justify-center text-gray-700 shadow"
                      >
                        &#10095;
                      </button>
                    </>
                  )}
                </div>

                <div className="grid grid-cols-5 gap-2 mb-4">
                  {hotelImages.slice(0, 5).map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImage(i)}
                      className={`relative h-16 sm:h-20 rounded-md overflow-hidden border-2 transition-colors ${
                        i === activeImage ? "border-blue-500" : "border-transparent"
                      }`}
                    >
                      <Image
                        src={getImageUrl(img, "240x180")}
                        alt={t("voucher.partner_hotels.photo_alt", { num: i + 1 })}
                        fill
                        sizes="120px"
                        className="object-cover"
                      />
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Hotel name + stars */}
            <h4 className="text-xl sm:text-2xl font-semibold text-black mb-2">
              {selectedHotel.hotel_name}
              <span className="inline-flex ml-2 text-yellow-400 align-middle">
                {[...Array(selectedHotel.star_rating || 3)].map((_, i) => (
                  <svg
                    key={i}
                    className="w-4 h-4 fill-yellow-400"
                    viewBox="0 0 576 512"
                  >
                    <path d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z" />
                  </svg>
                ))}
              </span>
            </h4>

            {/* TD-blocks 2026-07-02: amenity chips + context-appropriate
                cancellation, under the name/stars and before the address. */}
            {modalChips.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {modalChips.map((chip) => (
                  <AmenityChip key={chip.key} chip={chip} t={t} />
                ))}
              </div>
            )}
            {modalCancellation && (
              <div className="mb-3">
                <span className="inline-flex items-start gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-700">
                  <Undo2 className="w-3.5 h-3.5 mt-0.5 text-gray-500 flex-shrink-0" />
                  {modalCancellation}
                </span>
              </div>
            )}

            <div className="space-y-3 border-t border-gray-200 pt-3">
              {(selectedHotel.address || selectedHotel.city) && (
                <p className="text-gray-700 text-sm sm:text-base flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  {[selectedHotel.address, localizeHotelCity(selectedHotel.city, dealCity, locale)]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              )}

              {/* Main description (HTML from RateHawk) */}
              {selectedHotel.description && (
                <div
                  className="text-gray-700 text-sm sm:text-base leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(selectedHotel.description),
                  }}
                />
              )}

              {/* Structured description sections (description_struct).
                  Kartik #108 (2026-04-28): handle both legacy
                  `{title: string, paragraphs: string}` shape (RateHawk
                  imports + pre-cms#155) AND modern
                  `{title: MultiLangValue, content: MultiLangValue}`
                  shape (post cms#155 per-locale tabs). The
                  `resolveSectionField` helper at the top of this file
                  unwraps either shape via the current locale with
                  EN→NL→DE→FR fallback ladder. */}
              {Array.isArray(selectedHotel.description_struct) &&
                selectedHotel.description_struct.length > 0 &&
                selectedHotel.description_struct.map((feature, idx) => {
                  const titleText = resolveSectionField(feature?.title, locale);
                  // Modern field is `content`; legacy was `paragraphs`
                  // (RateHawk import shape). Try modern first, fall
                  // back to legacy. Either may be string OR multilang.
                  const bodyText =
                    resolveSectionField(feature?.content, locale) ||
                    resolveSectionField(feature?.paragraphs, locale);
                  // Skip the entire row when both title + body are empty
                  // — defensive against operator saving a blank section.
                  if (!titleText && !bodyText) return null;
                  return (
                    <div key={idx} className="py-2">
                      {titleText && (
                        <strong
                          className="text-gray-900 text-sm sm:text-base block mb-1"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(titleText) }}
                        />
                      )}
                      {bodyText && (
                        <span
                          className="text-gray-700 text-sm sm:text-base"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(bodyText) }}
                        />
                      )}
                    </div>
                  );
                })}

              {/* Per-hotel location map under the description (Dennis
                  2026-06-03). Renders a single pin at this hotel's
                  coordinates; returns null when lat/lng are missing. */}
              <HotelMiniMap
                hotel={selectedHotel}
                cityLabel={localizeHotelCity(selectedHotel.city, dealCity, locale)}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
